export interface DocItem {
  id: string;
  title: string;
  path?: string;
  // Previous URLs of the page, so old links and bookmarks keep resolving.
  urlAliases?: string[];
  children?: DocItem[];
}

export const HOME_ID = 'home';

export const docsStructure: DocItem[] = [
  {
    id: HOME_ID,
    title: 'Home',
  },
  {
    id: 'engines',
    title: 'AI Vendor Integration',
    children: [
      {
        id: 'avi-overview',
        title: 'Overview',
        children: [
          {
            id: 'avi-integration-summary',
            title: 'Integration Summary',
            path: 'ai-vendor-integration/01-overview/integration_summary.mdx',
            urlAliases: ['engines/index', 'ai-vendor-integration/01-overview/index'],
          },
          {
            id: 'avi-terminology',
            title: 'Terminology',
            path: 'ai-vendor-integration/01-overview/terminology.mdx',
          },
          {
            id: 'avi-getting-support',
            title: 'Getting Support',
            path: 'ai-vendor-integration/01-overview/getting_support.mdx',
          },
        ],
      },
      {
        id: 'avi-integration-requirements',
        title: 'Integration Requirements',
        children: [
          {
            id: 'avi-req-dev-os',
            title: 'Supported Developer Machine Operating Systems',
            path: 'ai-vendor-integration/03-integration-requirements/supported_developer_machine_operating_systems.mdx',
          },
          {
            id: 'avi-req-model-os',
            title: 'Supported Model Operating Systems',
            path: 'ai-vendor-integration/03-integration-requirements/supported_model_operating_systems.mdx',
          },
          {
            id: 'avi-req-architectures',
            title: 'Supported Container Architectures',
            path: 'ai-vendor-integration/03-integration-requirements/supported_container_architectures.mdx',
          },
          {
            id: 'sdk-compliance',
            title: 'Technical Requirements',
            path: 'ai-vendor-integration/03-integration-requirements/technical_requirements.mdx',
            urlAliases: ['engines/build/sdk/compliance', 'ai-vendor-integration/03-integration-requirements/compliance'],
          },
          {
            id: 'avi-req-security',
            title: 'Security Requirements',
            path: 'ai-vendor-integration/03-integration-requirements/security_requirements.mdx',
          },
          {
            id: 'avi-req-data-protection',
            title: 'Data Protection Requirements',
            path: 'ai-vendor-integration/03-integration-requirements/data_protection_requirements.mdx',
          },
          {
            id: 'avi-req-regulatory',
            title: 'Regulatory Requirements',
            path: 'ai-vendor-integration/03-integration-requirements/regulatory_requirements.mdx',
          },
        ],
      },
      {
        id: 'avi-architecture-runtime',
        title: 'Architecture and Runtime',
        children: [
          {
            id: 'sdk-overview',
            title: 'Overview',
            path: 'ai-vendor-integration/04-architecture-and-runtime/overview.mdx',
            urlAliases: ['engines/build/sdk/overview'],
          },
          {
            id: 'engines-architecture',
            title: 'System Architecture',
            path: 'ai-vendor-integration/04-architecture-and-runtime/architecture.mdx',
            urlAliases: ['engines/architecture'],
          },
          {
            id: 'sdk-support-frameworks',
            title: 'Supported Frameworks',
            path: 'ai-vendor-integration/04-architecture-and-runtime/supported_frameworks.mdx',
            urlAliases: ['engines/build/sdk/support/support', 'ai-vendor-integration/01-overview/support'],
          },
          {
            id: 'sdk-modes',
            title: 'Modes',
            children: [
              {
                id: 'sdk-modes-overview',
                title: 'Choosing a Mode',
                path: 'ai-vendor-integration/04-architecture-and-runtime/modes/overview.mdx',
                urlAliases: ['engines/build/sdk/modes/overview'],
              },
              {
                id: 'sdk-ephemeral',
                title: 'Ephemeral',
                path: 'ai-vendor-integration/04-architecture-and-runtime/modes/ephemeral.mdx',
                urlAliases: ['engines/build/sdk/modes/ephemeral'],
              },
              {
                id: 'sdk-server',
                title: 'Server',
                path: 'ai-vendor-integration/04-architecture-and-runtime/modes/server.mdx',
                urlAliases: ['engines/build/sdk/modes/server'],
              },
            ],
          },
          {
            id: 'wt-infra',
            title: 'Infrastructure',
            path: 'ai-vendor-integration/04-architecture-and-runtime/infrastructure.mdx',
            urlAliases: ['engines/build/work-through/infrastructure'],
          },
          {
            id: 'avi-readiness-health',
            title: 'Readiness and Health',
            path: 'ai-vendor-integration/04-architecture-and-runtime/readiness_and_health.mdx',
          },
          {
            id: 'avi-graceful-shutdown',
            title: 'Graceful Shutdown',
            path: 'ai-vendor-integration/04-architecture-and-runtime/graceful_shutdown.mdx',
          },
          {
            id: 'avi-cleaning-up',
            title: 'Cleaning Up',
            path: 'ai-vendor-integration/04-architecture-and-runtime/cleaning_up.mdx',
          },
          {
            id: 'avi-dicom-tags',
            title: 'DICOM Pseudonymisation and Tag Handling',
            path: 'ai-vendor-integration/04-architecture-and-runtime/dicom_pseudonymisation_and_tag_handling.mdx',
          },
        ],
      },
      {
        id: 'avi-data-transfer',
        title: 'Data Transfer',
        children: [
          {
            id: 'sdk-data-transfer',
            title: 'File I/O',
            path: 'ai-vendor-integration/05-data-transfer/file_io.mdx',
            urlAliases: ['engines/build/sdk/data_transfer', 'engines/build/sdk/data-transfer', 'ai-vendor-integration/05-data-transfer/data_transfer'],
          },
          {
            id: 'avi-dimse',
            title: 'DIMSE',
            path: 'ai-vendor-integration/05-data-transfer/dimse.mdx',
          },
        ],
      },
      {
        id: 'avi-build-your-engine',
        title: 'Build Your AI Model',
        children: [
          {
            id: 'wt-handson',
            title: 'Overview',
            path: 'ai-vendor-integration/06-build-your-engine/overview.mdx',
            urlAliases: ['engines/build/work-through/hands-on', 'ai-vendor-integration/06-build-your-engine/hands-on'],
          },
          {
            id: 'wt-manifest',
            title: 'Entrypoint and Versioning',
            path: 'ai-vendor-integration/06-build-your-engine/entrypoint_and_versioning.mdx',
            urlAliases: ['engines/build/work-through/create_engine_specification', 'engines/build/work-through/create-engine-manifest', 'ai-vendor-integration/06-build-your-engine/create_engine_specification'],
          },
          {
            id: 'avi-inputs-outputs',
            title: 'Reading Inputs and Writing Outputs',
            path: 'ai-vendor-integration/06-build-your-engine/reading_inputs_and_writing_outputs.mdx',
          },
          {
            id: 'avi-logs',
            title: 'Writing Useful Logs',
            path: 'ai-vendor-integration/06-build-your-engine/writing_useful_logs.mdx',
          },
          {
            id: 'sdk-error',
            title: 'Error Handling',
            path: 'ai-vendor-integration/06-build-your-engine/error_handling.mdx',
            urlAliases: ['engines/build/sdk/error_handling'],
          },
          {
            id: 'sdk-support',
            title: 'Framework Support',
            children: [
              {
                id: 'sdk-support-monai',
                title: 'MONAI',
                path: 'ai-vendor-integration/06-build-your-engine/framework-support/monai.mdx',
                urlAliases: ['engines/build/sdk/support/monai'],
              },
              {
                id: 'specs-ex-monai',
                title: 'MONAI Example',
                path: 'ai-vendor-integration/06-build-your-engine/framework-support/monai_example.mdx',
                urlAliases: ['engines/specs/examples/monai', 'engines/specs/examples/monai-examples', 'ai-vendor-integration/11-reference/specs/examples/monai'],
              },
            ],
          },
        ],
      },
      {
        id: 'avi-engine-output',
        title: 'AI Model Output',
        children: [
          {
            id: 'sdk-artefacts',
            title: 'Overview',
            path: 'ai-vendor-integration/07-engine-output/overview.mdx',
            urlAliases: ['engines/build/sdk/writing_artefacts', 'engines/build/sdk/artefacts', 'ai-vendor-integration/07-engine-output/writing_artefacts'],
          },
          {
            id: 'wt-enriched',
            title: 'deepc Engine Report',
            path: 'ai-vendor-integration/07-engine-output/engine_report.mdx',
            urlAliases: ['engines/build/work-through/enriched_output_data', 'engines/build/work-through/enriched-output-data', 'ai-vendor-integration/07-engine-output/enriched_output_data'],
          },
          {
            id: 'avi-snomed',
            title: 'SNOMED CT and Clinical Coding',
            path: 'ai-vendor-integration/07-engine-output/snomed_ct.mdx',
            urlAliases: ['engines/tools', 'ai-vendor-integration/11-reference/tools'],
          },
          {
            id: 'specs-report',
            title: 'Engine Report Schema',
            path: 'ai-vendor-integration/07-engine-output/engine_report_schema.mdx',
            urlAliases: ['engines/specs/engine-report', 'ai-vendor-integration/11-reference/specs/engine-report'],
          },
          {
            id: 'specs-ex-report',
            title: 'Example Engine Reports',
            path: 'ai-vendor-integration/07-engine-output/example_engine_reports.mdx',
            urlAliases: ['engines/specs/examples/engine_report', 'engines/specs/examples/engine-report', 'ai-vendor-integration/11-reference/specs/examples/engine_report'],
          },
        ],
      },
      {
        id: 'engines-publish',
        title: 'Submitting to deepc',
        children: [
          {
            id: 'pub-overview',
            title: 'Overview',
            path: 'ai-vendor-integration/08-submitting-to-deepc/overview.mdx',
            urlAliases: ['engines/build/publish/overview'],
          },
          {
            id: 'pub-portal',
            title: 'Submit AI',
            children: [
              {
                id: 'pub-portal-overview',
                title: 'Submission Introduction',
                path: 'ai-vendor-integration/08-submitting-to-deepc/portal/build_portal.mdx',
                urlAliases: ['engines/build/publish/portal/build_portal'],
              },
              {
                id: 'engines-cli',
                title: 'Get tetra CLI',
                path: 'ai-vendor-integration/08-submitting-to-deepc/portal/get_tetra_cli.mdx',
                urlAliases: ['engines/tetra_cli', 'engines/tetra-cli', 'ai-vendor-integration/08-submitting-to-deepc/tetra_cli'],
              },
              {
                id: 'pub-cli',
                title: 'How to Use Tetra CLI',
                path: 'ai-vendor-integration/08-submitting-to-deepc/portal/how_to_use_tetra_cli.mdx',
                urlAliases: ['engines/build/publish/cli', 'ai-vendor-integration/08-submitting-to-deepc/cli'],
              },
              {
                id: 'pub-portal-new',
                title: 'New Submission',
                path: 'ai-vendor-integration/08-submitting-to-deepc/portal/new_submission.mdx',
                urlAliases: ['engines/build/publish/portal/new_submission'],
              },
              {
                id: 'pub-portal-reports',
                title: 'Test Reports',
                path: 'ai-vendor-integration/08-submitting-to-deepc/portal/test_reports.mdx',
                urlAliases: ['engines/build/publish/portal/test_reports'],
              },
              {
                id: 'pub-portal-submissions',
                title: 'Submissions Overview',
                path: 'ai-vendor-integration/08-submitting-to-deepc/portal/submission_overview.mdx',
                urlAliases: ['engines/build/publish/portal/submission_overview', 'ai-vendor-integration/09-post-submission/submission_overview'],
              },
            ],
          },
        ],
      },
      {
        id: 'avi-post-submission',
        title: 'Post Submission',
        children: [
          {
            id: 'avi-monitoring',
            title: 'Monitoring and Analytics',
            path: 'ai-vendor-integration/09-post-submission/monitoring_and_analytics.mdx',
          },
          {
            id: 'avi-incidents',
            title: 'Incident Handling',
            path: 'ai-vendor-integration/09-post-submission/incident_handling.mdx',
          },
          {
            id: 'avi-updates-rollback',
            title: 'AI Model Updates and Rollback',
            path: 'ai-vendor-integration/09-post-submission/engine_updates_and_rollback.mdx',
          },
          {
            id: 'avi-deprecation',
            title: 'Version Deprecation Policy',
            path: 'ai-vendor-integration/09-post-submission/version_deprecation_policy.mdx',
          },
        ],
      },
      {
        id: 'avi-troubleshooting-faq',
        title: 'Troubleshooting / FAQ',
        children: [
          {
            id: 'engines-faq',
            title: 'FAQs',
            path: 'ai-vendor-integration/10-troubleshooting-faq/faq.mdx',
            urlAliases: ['engines/faq'],
          },
        ],
      },
      {
        id: 'avi-release-notes',
        title: 'Release Notes',
        children: [
          {
            id: 'engines-changelog',
            title: 'Changelog',
            path: 'ai-vendor-integration/12-release-notes/changelog.mdx',
            urlAliases: ['engines/changelog'],
          },
        ],
      },
    ],
  },
  {
    id: 'clinical',
    title: 'Clinical',
    children: [
      {
        id: 'clinical-overview',
        title: 'Overview',
        path: 'clinical/01_overview.md',
      },
      {
        id: 'clinical-webhooks-api',
        title: 'Webhooks',
        path: 'clinical/webhooks/01_api.md',
      },
      {
        id: 'clinical-fhir-api',
        title: 'FHIR',
        path: 'clinical/fhir/01_api.md',
      },
      {
        id: 'clinical-graphql-api',
        title: 'GraphQL',
        path: 'clinical/graphql/01_api.md',
      },
      {
        id: 'clinical-air-api',
        title: 'AIR Integration',
        path: 'clinical/AIR_integration/01_api.md',
        children: [
          {
            id: 'clinical-air-jwt',
            title: 'JWT Authentication',
            path: 'clinical/AIR_integration/02_jwt.md',
          },
        ],
      },
    ],
  },
];

export function docPathToUrl(path: string): string {
  return path.replace(/\.(md|mdx)$/, '');
}

function normalizeUrlPath(p: string): string {
  return p.replace(/-/g, '_');
}

export function findDocByUrlPath(
  items: DocItem[],
  urlPath: string,
): DocItem | undefined {
  const normalizedQuery = normalizeUrlPath(urlPath);
  for (const item of items) {
    if (item.path) {
      const itemUrl = docPathToUrl(item.path);
      if (itemUrl === urlPath || normalizeUrlPath(itemUrl) === normalizedQuery) return item;
    }
    if (
      item.urlAliases?.some(
        alias => alias === urlPath || normalizeUrlPath(alias) === normalizedQuery,
      )
    ) {
      return item;
    }
    if (item.children) {
      const found = findDocByUrlPath(item.children, urlPath);
      if (found) return found;
    }
  }
  return undefined;
}

export function findDocById(
  items: DocItem[],
  id: string,
): DocItem | undefined {
  for (const item of items) {
    if (item.id === id) return item;
    if (item.children) {
      const found = findDocById(item.children, id);
      if (found) return found;
    }
  }
  return undefined;
}

export function findFirstDoc(items: DocItem[]): DocItem | undefined {
  for (const item of items) {
    if (item.path) return item;
    if (item.children) {
      const found = findFirstDoc(item.children);
      if (found) return found;
    }
  }
  return undefined;
}

export function getAncestorIds(
  items: DocItem[],
  targetId: string,
  ancestors: string[] = [],
): string[] | undefined {
  for (const item of items) {
    if (item.id === targetId) return ancestors;
    if (item.children) {
      const result = getAncestorIds(item.children, targetId, [
        ...ancestors,
        item.id,
      ]);
      if (result) return result;
    }
  }
  return undefined;
}
